/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/laneControl.ts
 * FILE_ID: EMAILHUB-READERHEADER-LANES-CONTROL-001
 * PURPOSE: One class contract for the small controls the lane leaves render inline.
 * ROLE IN ARCHITECTURE: Class-token layer beside tokens.ts, which owns only numbers.
 * MAIN EXPORTS: LANE_CONTROL_CLASS, LANE_CONTROL_ICON, LANE_CONTROL_MIN_H.
 * NON-RESPONSIBILITIES: No colour. Each lane keeps its own semantic hue (amber
 *   review, blue job, orange structure) — this fixes geometry only.
 *
 * WHY: The four lane leaves each hand-rolled their chips, so the row mixed
 *   rounded-md against rounded-full, px-1.5 against px-2, and h-2.5 icons
 *   against h-3 icons. None of them set a height, so every one rendered at
 *   whatever py-0.5 + text-[10px] produced — around 18px, against TARGET.coarse
 *   of 44. Operator read the result as inconsistent chips that were hard to hit.
 *
 * HEIGHT CHOICE: min-h-6 is 24px, which is exactly the EllipsisSlot h-6 that
 *   already floors every lane row. Matching it makes the chips agree with the
 *   menu button without adding a pixel to the row, so CONTEXT_LANES_MAX (104)
 *   and the bodyBudget assertions built on it are untouched. The coarse floor
 *   likewise mirrors EllipsisSlot's pointer-coarse:h-11, so touch rows are
 *   already 44px before a chip asks for it.
 *
 * NOWRAP: min-h-6 is a FLOOR, so a control whose own label wrapped grew past
 *   it and took the whole lane row with it. Every one of these controls is a
 *   flex item inside LaneShell's shrinking chip run, so at tablet width the
 *   "+N role" pill was squeezed narrow enough to break onto two lines and
 *   rendered ~32px tall — the operator saw a fat pill in COMPANY and a band of
 *   dead space beside it in CLASS, which shares that grid row (2026-08-13).
 *   Labels here are 1-3 words; if one no longer fits it must clip against the
 *   lane's overflow-hidden and spill into the lane menu, which is what change
 *   #302 already decided for the chip RUN. Never drop this.
 */

/** Fine-pointer height floor (px) — the EllipsisSlot h-6 that floors the row. */
export const LANE_CONTROL_MIN_H = 24

/**
 * Geometry for an inline lane control. Compose with the lane's own colour
 * classes; never restate padding, radius, or height at the call site.
 *
 * `inline-flex` means a label that needs to truncate must sit in its own
 * `min-w-0 truncate` span — `truncate` on the flex container itself is inert.
 */
export const LANE_CONTROL_CLASS =
  'inline-flex min-h-6 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] leading-none transition-colors pointer-coarse:min-h-11'

/** Icon geometry inside a lane control. Never smaller — h-2.5 was illegible. */
export const LANE_CONTROL_ICON = 'h-3 w-3 shrink-0'
